import { Card, OrganizationAvatar, Tag, TruncatedText, XMoreWrapper } from "@components/library";
import { COLORS, FONTS } from "@constants";
import useFollowRequest from "@hooks/useFollowRequest";
import useImpression from "@hooks/useImpression";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { ConfidentialCompany, RequestForCard } from "@tsTypes/index";
import { LaunchableRequestStatus, REQUEST_USE_CASE_SHORT_LABELS } from "@tsTypes/requests";
import { MarketplaceRequest } from "@tsTypes/requests/marketplace";
import { PARTNERSHIP_MODEL_LABELS } from "@tsTypes/rfps";
import { track } from "@utils/appUtils";
import { getLaunchableRequestStatus, requestPageUrlPath } from "@utils/requestUtils";
import { filterByValidPartnershipModels } from "@utils/rfpUtils";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import RequestCardFooter from "./RequestCardFooter";

interface Props {
  request: RequestForCard | MarketplaceRequest;
  onClick?: (...args: any[]) => any;
  shouldShowUnsubscribeButton?: boolean;
  fromHref?: string;
}

const FADE_DURATION = 600;

const RfpCard = ({ request, onClick, shouldShowUnsubscribeButton = false, fromHref }: Props) => {
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [shouldFade, setShouldFade] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const timeoutRef = useRef<number | null>(null);

  const { title, company, use_case } = request;

  useImpression("RfpCard");

  const { handleFollowRequest, isUserFollowing } = useFollowRequest(request.id);

  // For the fade effect
  useEffect(() => {
    if (shouldFade) {
      // Wait for transition to complete
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);
      }, FADE_DURATION);
    } else {
      setIsVisible(true);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [shouldFade]);

  const handleNotify = () => {
    if (getLaunchableRequestStatus(request) !== LaunchableRequestStatus.COMING_SOON) return;

    handleFollowRequest();

    track(SegmentEventName.CLICK, {
      ui_component: "Coming soon request card",
      react_component: "RfpCardNew",
      logged_in: false,
    });
  };

  const isComingSoon = getLaunchableRequestStatus(request) === LaunchableRequestStatus.COMING_SOON;
  const isPrivate = !("company_name" in request.company);

  const partnershipModels = filterByValidPartnershipModels(
    ("partnership_models" in request.requestable && request.requestable.partnership_models) || []
  );

  const CardLink = isComingSoon || shouldShowUnsubscribeButton ? DisabledCardLink : ActiveCardLink;

  return (
    <FadeWrapper shouldFade={shouldFade} isVisible={isVisible}>
      <CardLink
        to={{
          pathname: requestPageUrlPath(request),
          state: { fromHref },
        }}
      >
        <Card
          padding="0px"
          showHoverBorder
          onClick={onClick ?? handleNotify}
          onMouseEnter={() => setIsCardHovered(true)}
          onMouseLeave={() => setIsCardHovered(false)}
          data-testid="rfp-card"
        >
          <MainContent>
            <Company data-testid="company-name">
              <OrganizationAvatar
                isPrivate={isPrivate}
                imageUrl={"avatar" in company ? company.avatar.url : null}
                size="sm"
              />
              {isPrivate ? (
                (company as ConfidentialCompany).private_alias ?? "Private"
              ) : (
                <TruncatedText
                  text={"company_name" in company ? company.company_name : ""}
                  font={FONTS.SEMIBOLD_1}
                  textWidth="calc(100% - 48px)"
                  tooltipWidth="200px"
                />
              )}
            </Company>
            <Title data-testid="title">
              <TruncatedText text={title} lines={5} font={FONTS.MEDIUM_1} />
            </Title>
            <XMoreWrapper
              maxLines={2}
              color="#0F9158" // These greens are temporarily hardcoded until we transition to the new design system color scheme
              backgroundColor="#EAF7F4"
              data-testid="keywords"
            >
              {[
                ...(use_case
                  ? [
                      <Tag
                        key={use_case}
                        content={REQUEST_USE_CASE_SHORT_LABELS[use_case]}
                        color={COLORS.BLUE_600}
                        backgroundColor={COLORS.BLUE_LIGHT_300}
                      />,
                    ]
                  : []),
                ...partnershipModels.map((keyword) => (
                  <Tag
                    key={keyword}
                    content={PARTNERSHIP_MODEL_LABELS[keyword]}
                    color="#0F9158"
                    backgroundColor="#EAF7F4"
                  />
                )),
              ]}
            </XMoreWrapper>
          </MainContent>
          <RequestCardFooter
            request={request}
            isUserFollowing={isUserFollowing ?? false}
            isCardHovered={isCardHovered}
            setShouldFade={setShouldFade}
            shouldShowUnsubscribeButton={shouldShowUnsubscribeButton}
          />
        </Card>
      </CardLink>
    </FadeWrapper>
  );
};

export default RfpCard;

const FadeWrapper = styled.div<{ shouldFade: boolean; isVisible: boolean }>`
  ${({ isVisible }) => !isVisible && "display: none"};
  opacity: ${({ shouldFade }) => (shouldFade ? 0 : 1)};
  transition: opacity ${FADE_DURATION}ms ease-out;
`;

const ActiveCardLink = styled(Link)`
  display: block;
  :visited,
  :link {
    color: ${COLORS.BLACK};
    text-decoration: none;
  }
`;
const DisabledCardLink = styled.div`
  display: block;
  color: ${COLORS.BLACK};
  text-decoration: none;
`;
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 320px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: 24px 24px 16px;
  border-radius: ${({ borderRadius }) => borderRadius};
`;
const Company = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
  ${FONTS.SEMIBOLD_1};
`;
const Title = styled.div`
  height: 54px;
  margin-bottom: auto;
  ${FONTS.MEDIUM_1};
`;
